import React, { useState } from 'react';
import './SelfDevelopmentPage.css';

const SelfDevelopmentPage: React.FC = () => {
  // Books state
  const [books, setBooks] = useState<Array<{id: number, title: string, author: string, status: 'reading' | 'completed' | 'to-read', startDate?: string, endDate?: string, rating?: number, notes: string, quotes: string[]}>>([]);
  const [showBookModal, setShowBookModal] = useState(false);
  const [newBook, setNewBook] = useState({title: '', author: '', status: 'to-read' as 'reading' | 'completed' | 'to-read', rating: '', notes: '', quotes: ['']});

  // Courses state
  const [courses, setCourses] = useState<Array<{id: number, name: string, platform: string, status: 'not-started' | 'in-progress' | 'completed', progress: number, startDate?: string, endDate?: string, notes: string}>>([]);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [newCourse, setNewCourse] = useState({name: '', platform: '', status: 'not-started' as 'not-started' | 'in-progress' | 'completed', progress: 0, notes: ''});

  // Languages state
  const [languages, setLanguages] = useState<Array<{id: number, language: string, level: 'beginner' | 'intermediate' | 'advanced' | 'native', studyTime: number, lastPractice?: string, streak: number}>>([]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [newLanguage, setNewLanguage] = useState({language: '', level: 'beginner' as 'beginner' | 'intermediate' | 'advanced' | 'native'});

  // Creative projects state
  const [creativeProjects, setCreativeProjects] = useState<Array<{id: number, name: string, type: 'art' | 'writing' | 'music' | 'craft' | 'other', status: 'planning' | 'in-progress' | 'completed', description: string, startDate?: string, endDate?: string}>>([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({name: '', type: 'art' as 'art' | 'writing' | 'music' | 'craft' | 'other', status: 'planning' as 'planning' | 'in-progress' | 'completed', description: ''});

  // Journal entries state
  const [journalEntries, setJournalEntries] = useState<Array<{id: number, date: string, title: string, content: string, mood: 'happy' | 'neutral' | 'sad' | 'excited' | 'anxious', tags: string[]}>>([]);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [newJournalEntry, setNewJournalEntry] = useState({title: '', content: '', mood: 'neutral' as 'happy' | 'neutral' | 'sad' | 'excited' | 'anxious', tags: ['']});

  // Cultural activities state
  const [culturalActivities, setCulturalActivities] = useState<Array<{id: number, type: 'concert' | 'exhibition' | 'theater' | 'movie' | 'book' | 'other', title: string, date: string, rating?: number, notes: string, location?: string}>>([]);
  const [showCulturalModal, setShowCulturalModal] = useState(false);
  const [newCulturalActivity, setNewCulturalActivity] = useState({type: 'concert' as 'concert' | 'exhibition' | 'theater' | 'movie' | 'book' | 'other', title: '', date: '', rating: '', notes: '', location: ''});

  // Fitness goals state
  const [fitnessGoals, setFitnessGoals] = useState<Array<{id: number, type: 'strength' | 'cardio' | 'flexibility' | 'endurance', name: string, target: string, current: string, deadline?: string, status: 'active' | 'completed' | 'paused'}>>([]);
  const [showFitnessModal, setShowFitnessModal] = useState(false);
  const [newFitnessGoal, setNewFitnessGoal] = useState({type: 'strength' as 'strength' | 'cardio' | 'flexibility' | 'endurance', name: '', target: '', current: '', deadline: ''});

  // Reflection entries state
  const [reflectionEntries, setReflectionEntries] = useState<Array<{id: number, date: string, questions: string[], answers: string[], insights: string, actionItems: string[]}>>([]);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [newReflectionEntry, setNewReflectionEntry] = useState({questions: ['Что хорошего произошло сегодня?', 'Что можно улучшить?', 'За что я благодарна?'], answers: ['', '', ''], insights: '', actionItems: ['']});

  // Social skills state
  const [socialSkills, setSocialSkills] = useState<Array<{id: number, skill: string, description: string, practiceSessions: number, lastPractice?: string, improvement: 'beginner' | 'developing' | 'good' | 'excellent'}>>([]);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [newSocialSkill, setNewSocialSkill] = useState({skill: '', description: '', improvement: 'beginner' as 'beginner' | 'developing' | 'good' | 'excellent'});

  // Ideas state
  const [ideas, setIdeas] = useState<Array<{id: number, title: string, description: string, category: 'business' | 'creative' | 'personal' | 'learning' | 'other', status: 'idea' | 'planning' | 'in-progress' | 'completed', dateAdded: string, tags: string[]}>>([]);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [newIdea, setNewIdea] = useState({title: '', description: '', category: 'creative' as 'business' | 'creative' | 'personal' | 'learning' | 'other', tags: ['']});

  // Achievements state
  const [achievements, setAchievements] = useState<Array<{id: number, title: string, description: string, date: string, category: 'career' | 'personal' | 'health' | 'learning' | 'creative' | 'social', importance: 'small' | 'medium' | 'big' | 'milestone'}>>([]);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [newAchievement, setNewAchievement] = useState({title: '', description: '', category: 'personal' as 'career' | 'personal' | 'health' | 'learning' | 'creative' | 'social', importance: 'medium' as 'small' | 'medium' | 'big' | 'milestone'});

  // Dreams state
  const [dreams, setDreams] = useState<Array<{id: number, dream: string, description: string, category: 'travel' | 'career' | 'personal' | 'relationships' | 'creative' | 'other', priority: 'low' | 'medium' | 'high', steps: string[], timeline?: string}>>([]);
  const [showDreamModal, setShowDreamModal] = useState(false);
  const [newDream, setNewDream] = useState({dream: '', description: '', category: 'personal' as 'travel' | 'career' | 'personal' | 'relationships' | 'creative' | 'other', priority: 'medium' as 'low' | 'medium' | 'high', steps: [''], timeline: ''});


  // Optional sections visibility state
  const [enabledSections, setEnabledSections] = useState({
    courses: false,
    languages: false,
    creativeProjects: false,
    culturalActivities: false,
    fitnessGoals: false,
    socialSkills: false,
    dreams: false
  });

  // Helper functions
  const addQuote = () => {
    setNewBook({...newBook, quotes: [...newBook.quotes, '']});
  };

  const updateQuote = (index: number, value: string) => {
    const newQuotes = [...newBook.quotes];
    newQuotes[index] = value;
    setNewBook({...newBook, quotes: newQuotes});
  };

  const addJournalTag = () => {
    setNewJournalEntry({...newJournalEntry, tags: [...newJournalEntry.tags, '']});
  };

  const updateJournalTag = (index: number, value: string) => {
    const newTags = [...newJournalEntry.tags];
    newTags[index] = value;
    setNewJournalEntry({...newJournalEntry, tags: newTags});
  };

  const addReflectionActionItem = () => {
    setNewReflectionEntry({...newReflectionEntry, actionItems: [...newReflectionEntry.actionItems, '']});
  };

  const updateReflectionActionItem = (index: number, value: string) => {
    const newActionItems = [...newReflectionEntry.actionItems];
    newActionItems[index] = value;
    setNewReflectionEntry({...newReflectionEntry, actionItems: newActionItems});
  };

  const addIdeaTag = () => {
    setNewIdea({...newIdea, tags: [...newIdea.tags, '']});
  };

  const updateIdeaTag = (index: number, value: string) => {
    const newTags = [...newIdea.tags];
    newTags[index] = value;
    setNewIdea({...newIdea, tags: newTags});
  };

  const addDreamStep = () => {
    setNewDream({...newDream, steps: [...newDream.steps, '']});
  };



  const updateDreamStep = (index: number, value: string) => {
    const newSteps = [...newDream.steps];
    newSteps[index] = value;
    setNewDream({...newDream, steps: newSteps});
  };

  // Function to toggle section visibility
  const toggleSection = (section: keyof typeof enabledSections) => {
    setEnabledSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="self-development-page">
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Саморазвитие</h1>
          <p className="page-subtitle">Инвестируй в себя и становись лучше каждый день</p>
        </div>
        
        <div className="page-content">
          <div className="section">
            <h2 className="section-title">Обучение и знания</h2>
            
            {/* Books Section */}
            <div className="development-section">
              <div className="section-header">
                <h3>📚 Чтение книг</h3>
                <button onClick={() => setShowBookModal(true)} className="btn btn-primary">Добавить книгу</button>
              </div>
              
              <div className="books-overview">
                {books.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните читать и добавляйте книги в свой список</p>
                  </div>
                ) : (
                  <div className="books-list">
                    {books.slice(0, 4).map(book => (
                      <div key={book.id} className={`book-item ${book.status}`}>
                        <div className="book-info">
                          <h4>{book.title}</h4>
                          <p>Автор: {book.author}</p>
                          {book.rating && <div className="book-rating">⭐ {book.rating}/5</div>}
                          {book.quotes.length > 0 && (
                            <div className="book-quotes">
                              <span className="quotes-count">{book.quotes.length} цитат</span>
                            </div>
                          )}
                        </div>
                        <span className={`book-status ${book.status}`}>
                          {book.status === 'reading' ? 'Читаю' :
                           book.status === 'completed' ? 'Прочитано' : 'К прочтению'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Courses Section - Optional */}
            {enabledSections.courses && (
            <div className="development-section">
              <div className="section-header">
                <h3>🎓 Курсы и обучение</h3>
                <button onClick={() => setShowCourseModal(true)} className="btn btn-primary">Записаться на курс</button>
              </div>
              
              <div className="courses-overview">
                {courses.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните изучать новые навыки</p>
                  </div>
                ) : (
                  <div className="courses-list">
                    {courses.slice(0, 3).map(course => (
                      <div key={course.id} className={`course-item ${course.status}`}>
                        <div className="course-info">
                          <h4>{course.name}</h4>
                          <p>Платформа: {course.platform}</p>
                          <div className="course-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{width: `${course.progress}%`}}
                              ></div>
                            </div>
                            <span className="progress-text">{course.progress}%</span>
                          </div>
                        </div>
                        <span className={`course-status ${course.status}`}>
                          {course.status === 'in-progress' ? 'Изучаю' :
                           course.status === 'completed' ? 'Завершен' : 'Не начат'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Languages Section - Optional */}
            {enabledSections.languages && (
            <div className="development-section">
              <div className="section-header">
                <h3>🌍 Языки</h3>
                <button onClick={() => setShowLanguageModal(true)} className="btn btn-primary">Начать изучение</button>
              </div>
              
              <div className="languages-overview">
                {languages.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте языки для изучения</p>
                  </div>
                ) : (
                  <div className="languages-list">
                    {languages.map(language => (
                      <div key={language.id} className="language-item">
                        <div className="language-info">
                          <h4>{language.language}</h4>
                          <p>Уровень: {language.level}</p>
                          <div className="language-stats">
                            <span className="study-time">⏱️ {language.studyTime} часов</span>
                            <span className="streak">🔥 {language.streak} дней</span>
                          </div>
                        </div>
                        <button 
                          onClick={() => {
                            setLanguages(languages.map(l => 
                              l.id === language.id 
                                ? {...l, studyTime: l.studyTime + 0.5, streak: l.streak + 1, lastPractice: new Date().toISOString().split('T')[0]}
                                : l
                            ));
                          }}
                          className="practice-btn"
                        >
                          Практиковать
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Творчество и хобби</h2>
            
            {/* Creative Projects - Optional */}
            {enabledSections.creativeProjects && (
            <div className="development-section">
              <div className="section-header">
                <h3>🎨 Творческие проекты</h3>
                <button onClick={() => setShowProjectModal(true)} className="btn btn-secondary">Создать проект</button>
              </div>
              
              <div className="projects-overview">
                {creativeProjects.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните творческий проект</p>
                  </div>
                ) : (
                  <div className="projects-list">
                    {creativeProjects.slice(0, 3).map(project => (
                      <div key={project.id} className={`project-item ${project.status}`}>
                        <div className="project-info">
                          <h4>{project.name}</h4>
                          <p>Тип: {project.type}</p>
                          <p className="project-description">{project.description}</p>
                        </div>
                        <span className={`project-status ${project.status}`}>
                          {project.status === 'planning' ? 'Планирование' :
                           project.status === 'in-progress' ? 'В работе' : 'Завершен'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Journal */}
            <div className="development-section">
              <div className="section-header">
                <h3>✍️ Дневник и заметки</h3>
                <button onClick={() => setShowJournalModal(true)} className="btn btn-secondary">Написать заметку</button>
              </div>
              
              <div className="journal-overview">
                {journalEntries.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните вести дневник</p>
                  </div>
                ) : (
                  <div className="journal-list">
                    {journalEntries.slice(0, 3).map(entry => (
                      <div key={entry.id} className="journal-item">
                        <div className="journal-info">
                          <h4>{entry.title}</h4>
                          <p className="journal-date">{entry.date}</p>
                          <p className="journal-content">{entry.content.length > 100 ? entry.content.substring(0, 100) + '...' : entry.content}</p>
                          {entry.tags.length > 0 && (
                            <div className="journal-tags">
                              {entry.tags.filter(tag => tag.trim()).map((tag, index) => (
                                <span key={index} className="tag">#{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className={`journal-mood ${entry.mood}`}>
                          {entry.mood === 'happy' ? '😊' :
                           entry.mood === 'neutral' ? '😐' :
                           entry.mood === 'sad' ? '😔' :
                           entry.mood === 'excited' ? '🤩' : '😰'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Cultural Activities - Optional */}
            {enabledSections.culturalActivities && (
            <div className="development-section">
              <div className="section-header">
                <h3>🎵 Музыка и искусство</h3>
                <button onClick={() => setShowCulturalModal(true)} className="btn btn-secondary">Добавить в список</button>
              </div>
              
              <div className="cultural-overview">
                {culturalActivities.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте культурные мероприятия</p>
                  </div>
                ) : (
                  <div className="cultural-list">
                    {culturalActivities.slice(0, 3).map(activity => (
                      <div key={activity.id} className="cultural-item">
                        <div className="cultural-info">
                          <h4>{activity.title}</h4>
                          <p>Тип: {activity.type} • {activity.date}</p>
                          {activity.location && <p>📍 {activity.location}</p>}
                          {activity.rating && <div className="cultural-rating">⭐ {activity.rating}/5</div>}
                        </div>
                        <span className="cultural-type">{activity.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Личностный рост</h2>
            
            {/* Fitness Goals - Optional */}
            {enabledSections.fitnessGoals && (
            <div className="development-section">
              <div className="section-header">
                <h3>💪 Спорт и фитнес</h3>
                <button onClick={() => setShowFitnessModal(true)} className="btn btn-primary">Добавить цель</button>
              </div>
              
              <div className="fitness-overview">
                {fitnessGoals.length === 0 ? (
                  <div className="empty-state">
                    <p>Поставьте фитнес-цели</p>
                  </div>
                ) : (
                  <div className="fitness-list">
                    {fitnessGoals.slice(0, 3).map(goal => (
                      <div key={goal.id} className={`fitness-item ${goal.status}`}>
                        <div className="fitness-info">
                          <h4>{goal.name}</h4>
                          <p>Тип: {goal.type}</p>
                          <div className="goal-progress">
                            <span>Текущий: {goal.current}</span>
                            <span>Цель: {goal.target}</span>
                          </div>
                          {goal.deadline && <p>Дедлайн: {goal.deadline}</p>}
                        </div>
                        <span className={`goal-status ${goal.status}`}>
                          {goal.status === 'active' ? 'Активно' :
                           goal.status === 'completed' ? 'Достигнуто' : 'Приостановлено'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Reflection */}
            <div className="development-section">
              <div className="section-header">
                <h3>🧠 Мышление и рефлексия</h3>
                <button onClick={() => setShowReflectionModal(true)} className="btn btn-primary">Начать рефлексию</button>
              </div>
              
              <div className="reflection-overview">
                {reflectionEntries.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните практиковать рефлексию</p>
                  </div>
                ) : (
                  <div className="reflection-list">
                    {reflectionEntries.slice(0, 2).map(entry => (
                      <div key={entry.id} className="reflection-item">
                        <div className="reflection-info">
                          <h4>Рефлексия от {entry.date}</h4>
                          {entry.insights && (
                            <p className="reflection-insights">
                              <strong>Инсайты:</strong> {entry.insights}
                            </p>
                          )}
                          {entry.actionItems.length > 0 && (
                            <div className="action-items">
                              <strong>Планы действий:</strong>
                              <ul>
                                {entry.actionItems.filter(item => item.trim()).slice(0, 2).map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Social Skills - Optional */}
            {enabledSections.socialSkills && (
            <div className="development-section">
              <div className="section-header">
                <h3>🤝 Социальные навыки</h3>
                <button onClick={() => setShowSkillModal(true)} className="btn btn-primary">Добавить навык</button>
              </div>
              
              <div className="skills-overview">
                {socialSkills.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте навыки для развития</p>
                  </div>
                ) : (
                  <div className="skills-list">
                    {socialSkills.map(skill => (
                      <div key={skill.id} className="skill-item">
                        <div className="skill-info">
                          <h4>{skill.skill}</h4>
                          <p>{skill.description}</p>
                          <div className="skill-stats">
                            <span>Практика: {skill.practiceSessions} раз</span>
                            <span className={`skill-level ${skill.improvement}`}>
                              {skill.improvement === 'beginner' ? 'Начальный' :
                               skill.improvement === 'developing' ? 'Развивающийся' :
                               skill.improvement === 'good' ? 'Хороший' : 'Отличный'}
                            </span>
                          </div>
                        </div>
                        <button 
                          onClick={() => {
                            setSocialSkills(socialSkills.map(s => 
                              s.id === skill.id 
                                ? {...s, practiceSessions: s.practiceSessions + 1, lastPractice: new Date().toISOString().split('T')[0]}
                                : s
                            ));
                          }}
                          className="practice-skill-btn"
                        >
                          Практиковать
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Вдохновение и мотивация</h2>
            
            {/* Ideas */}
            <div className="development-section">
              <div className="section-header">
                <h3>💡 Идеи и вдохновение</h3>
                <button onClick={() => setShowIdeaModal(true)} className="btn btn-secondary">Сохранить идею</button>
              </div>
              
              <div className="ideas-overview">
                {ideas.length === 0 ? (
                  <div className="empty-state">
                    <p>Сохраняйте интересные идеи</p>
                  </div>
                ) : (
                  <div className="ideas-list">
                    {ideas.slice(0, 3).map(idea => (
                      <div key={idea.id} className={`idea-item ${idea.status}`}>
                        <div className="idea-info">
                          <h4>{idea.title}</h4>
                          <p className="idea-description">{idea.description}</p>
                          <div className="idea-meta">
                            <span className="idea-category">{idea.category}</span>
                            <span className="idea-date">{idea.dateAdded}</span>
                          </div>
                          {idea.tags.length > 0 && (
                            <div className="idea-tags">
                              {idea.tags.filter(tag => tag.trim()).map((tag, index) => (
                                <span key={index} className="tag">#{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className={`idea-status ${idea.status}`}>
                          {idea.status === 'idea' ? 'Идея' :
                           idea.status === 'planning' ? 'Планирование' :
                           idea.status === 'in-progress' ? 'В работе' : 'Завершено'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Achievements */}
            <div className="development-section">
              <div className="section-header">
                <h3>🎯 Достижения</h3>
                <button onClick={() => setShowAchievementModal(true)} className="btn btn-secondary">Записать достижение</button>
              </div>
              
              <div className="achievements-overview">
                {achievements.length === 0 ? (
                  <div className="empty-state">
                    <p>Отмечайте свои победы</p>
                  </div>
                ) : (
                  <div className="achievements-list">
                    {achievements.slice(0, 3).map(achievement => (
                      <div key={achievement.id} className={`achievement-item ${achievement.importance}`}>
                        <div className="achievement-info">
                          <h4>{achievement.title}</h4>
                          <p>{achievement.description}</p>
                          <div className="achievement-meta">
                            <span className="achievement-category">{achievement.category}</span>
                            <span className="achievement-date">{achievement.date}</span>
                          </div>
                        </div>
                        <span className={`achievement-badge ${achievement.importance}`}>
                          {achievement.importance === 'small' ? 'Маленькая победа' :
                           achievement.importance === 'medium' ? 'Достижение' :
                           achievement.importance === 'big' ? 'Большой успех' : 'Важная веха'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Dreams - Optional */}
            {enabledSections.dreams && (
            <div className="development-section">
              <div className="section-header">
                <h3>🌟 Мечты и цели</h3>
                <button onClick={() => setShowDreamModal(true)} className="btn btn-secondary">Добавить мечту</button>
              </div>
              
              <div className="dreams-overview">
                {dreams.length === 0 ? (
                  <div className="empty-state">
                    <p>Записывайте свои мечты</p>
                  </div>
                ) : (
                  <div className="dreams-list">
                    {dreams.slice(0, 3).map(dream => (
                      <div key={dream.id} className={`dream-item ${dream.priority}`}>
                        <div className="dream-info">
                          <h4>{dream.dream}</h4>
                          <p>{dream.description}</p>
                          <div className="dream-meta">
                            <span className="dream-category">{dream.category}</span>
                            {dream.timeline && <span className="dream-timeline">⏰ {dream.timeline}</span>}
                          </div>
                          {dream.steps.length > 0 && (
                            <div className="dream-steps">
                              <strong>Шаги к мечте:</strong>
                              <ul>
                                {dream.steps.filter(step => step.trim()).slice(0, 2).map((step, index) => (
                                  <li key={index}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        <span className={`dream-priority ${dream.priority}`}>
                          {dream.priority === 'low' ? 'Низкий приоритет' :
                           dream.priority === 'medium' ? 'Средний приоритет' : 'Высокий приоритет'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Overview - Dynamic based on enabled sections */}
      <div className="progress-overview">
        <h2>Мой прогресс</h2>
        <div className="progress-grid">
          {/* Always show basic sections */}
          <div className="progress-item">
            <span className="progress-icon">📚</span>
            <div className="progress-info">
              <span className="progress-number">{books.filter(b => b.status === 'completed').length}</span>
              <span className="progress-label">книг прочитано</span>
            </div>
          </div>
          <div className="progress-item">
            <span className="progress-icon">✍️</span>
            <div className="progress-info">
              <span className="progress-number">{journalEntries.length}</span>
              <span className="progress-label">записей в дневнике</span>
            </div>
          </div>
          <div className="progress-item">
            <span className="progress-icon">🧠</span>
            <div className="progress-info">
              <span className="progress-number">{reflectionEntries.length}</span>
              <span className="progress-label">рефлексий</span>
            </div>
          </div>
          <div className="progress-item">
            <span className="progress-icon">💡</span>
            <div className="progress-info">
              <span className="progress-number">{ideas.length}</span>
              <span className="progress-label">идей сохранено</span>
            </div>
          </div>
          <div className="progress-item">
            <span className="progress-icon">🎯</span>
            <div className="progress-info">
              <span className="progress-number">{achievements.length}</span>
              <span className="progress-label">достижений</span>
            </div>
          </div>
          
          {/* Show optional sections only if enabled */}
          {enabledSections.courses && (
            <div className="progress-item">
              <span className="progress-icon">🎓</span>
              <div className="progress-info">
                <span className="progress-number">{courses.filter(c => c.status === 'completed').length}</span>
                <span className="progress-label">курсов завершено</span>
              </div>
            </div>
          )}
          
          {enabledSections.languages && (
            <div className="progress-item">
              <span className="progress-icon">🌍</span>
              <div className="progress-info">
                <span className="progress-number">{languages.reduce((sum, l) => sum + l.studyTime, 0)}</span>
                <span className="progress-label">часов изучения языков</span>
              </div>
            </div>
          )}
          
          {enabledSections.creativeProjects && (
            <div className="progress-item">
              <span className="progress-icon">🎨</span>
              <div className="progress-info">
                <span className="progress-number">{creativeProjects.filter(p => p.status === 'completed').length}</span>
                <span className="progress-label">проектов завершено</span>
              </div>
            </div>
          )}
          
          {enabledSections.culturalActivities && (
            <div className="progress-item">
              <span className="progress-icon">🎵</span>
              <div className="progress-info">
                <span className="progress-number">{culturalActivities.length}</span>
                <span className="progress-label">культурных мероприятий</span>
              </div>
            </div>
          )}
          
          {enabledSections.fitnessGoals && (
            <div className="progress-item">
              <span className="progress-icon">💪</span>
              <div className="progress-info">
                <span className="progress-number">{fitnessGoals.filter(g => g.status === 'completed').length}</span>
                <span className="progress-label">целей достигнуто</span>
              </div>
            </div>
          )}
          
          {enabledSections.socialSkills && (
            <div className="progress-item">
              <span className="progress-icon">🤝</span>
              <div className="progress-info">
                <span className="progress-number">{socialSkills.reduce((sum, s) => sum + s.practiceSessions, 0)}</span>
                <span className="progress-label">практических сессий</span>
              </div>
            </div>
          )}
          
          {enabledSections.dreams && (
            <div className="progress-item">
              <span className="progress-icon">🌟</span>
              <div className="progress-info">
                <span className="progress-number">{dreams.length}</span>
                <span className="progress-label">мечты и цели</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sections Manager */}
      <div className="sections-manager">
        <h3>Настройка разделов</h3>
        <p>Выберите разделы, которые хотите видеть в своем разделе саморазвития:</p>
        <div className="sections-toggles">
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.courses}
              onChange={() => toggleSection('courses')}
            />
            <span>🎓 Курсы и обучение</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.languages}
              onChange={() => toggleSection('languages')}
            />
            <span>🌍 Языки</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.creativeProjects}
              onChange={() => toggleSection('creativeProjects')}
            />
            <span>🎨 Творческие проекты</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.culturalActivities}
              onChange={() => toggleSection('culturalActivities')}
            />
            <span>🎵 Музыка и искусство</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.fitnessGoals}
              onChange={() => toggleSection('fitnessGoals')}
            />
            <span>💪 Спорт и фитнес</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.socialSkills}
              onChange={() => toggleSection('socialSkills')}
            />
            <span>🤝 Социальные навыки</span>
          </label>
          <label className="section-toggle">
            <input
              type="checkbox"
              checked={enabledSections.dreams}
              onChange={() => toggleSection('dreams')}
            />
            <span>🌟 Мечты и цели</span>
          </label>
        </div>
      </div>

      {/* Modals */}
      {/* Book Modal */}
      {showBookModal && (
        <div className="modal-overlay" onClick={() => setShowBookModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить книгу</h3>
              <button onClick={() => setShowBookModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название книги"
                value={newBook.title}
                onChange={(e) => setNewBook({...newBook, title: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Автор"
                value={newBook.author}
                onChange={(e) => setNewBook({...newBook, author: e.target.value})}
                className="modal-input"
              />
              <select
                value={newBook.status}
                onChange={(e) => setNewBook({...newBook, status: e.target.value as any})}
                className="modal-select"
              >
                <option value="to-read">К прочтению</option>
                <option value="reading">Читаю</option>
                <option value="completed">Прочитано</option>
              </select>
              {newBook.status === 'completed' && (
                <input
                  type="number"
                  placeholder="Оценка (1-5)"
                  min="1"
                  max="5"
                  value={newBook.rating}
                  onChange={(e) => setNewBook({...newBook, rating: e.target.value})}
                  className="modal-input"
                />
              )}
              <textarea
                placeholder="Заметки о книге"
                value={newBook.notes}
                onChange={(e) => setNewBook({...newBook, notes: e.target.value})}
                className="modal-textarea"
              />
              <div className="quotes-section">
                <h4>Любимые цитаты:</h4>
                {newBook.quotes.map((quote, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Цитата ${index + 1}`}
                    value={quote}
                    onChange={(e) => updateQuote(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addQuote} className="btn btn-secondary">Добавить цитату</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBookModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newBook.title && newBook.author) {
                  setBooks([...books, {
                    id: Date.now(),
                    title: newBook.title,
                    author: newBook.author,
                    status: newBook.status,
                    rating: newBook.rating ? parseInt(newBook.rating) : undefined,
                    notes: newBook.notes,
                    quotes: newBook.quotes.filter(quote => quote.trim())
                  }]);
                  setNewBook({title: '', author: '', status: 'to-read', rating: '', notes: '', quotes: ['']});
                  setShowBookModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Course Modal */}
      {showCourseModal && (
        <div className="modal-overlay" onClick={() => setShowCourseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Записаться на курс</h3>
              <button onClick={() => setShowCourseModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название курса"
                value={newCourse.name}
                onChange={(e) => setNewCourse({...newCourse, name: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Платформа (Coursera, Udemy, etc.)"
                value={newCourse.platform}
                onChange={(e) => setNewCourse({...newCourse, platform: e.target.value})}
                className="modal-input"
              />
              <select
                value={newCourse.status}
                onChange={(e) => setNewCourse({...newCourse, status: e.target.value as any})}
                className="modal-select"
              >
                <option value="not-started">Не начат</option>
                <option value="in-progress">Изучаю</option>
                <option value="completed">Завершен</option>
              </select>
              {newCourse.status !== 'not-started' && (
                <input
                  type="number"
                  placeholder="Прогресс (%)"
                  min="0"
                  max="100"
                  value={newCourse.progress}
                  onChange={(e) => setNewCourse({...newCourse, progress: parseInt(e.target.value) || 0})}
                  className="modal-input"
                />
              )}
              <textarea
                placeholder="Заметки о курсе"
                value={newCourse.notes}
                onChange={(e) => setNewCourse({...newCourse, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCourseModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newCourse.name && newCourse.platform) {
                  setCourses([...courses, {
                    id: Date.now(),
                    name: newCourse.name,
                    platform: newCourse.platform,
                    status: newCourse.status,
                    progress: newCourse.progress,
                    notes: newCourse.notes
                  }]);
                  setNewCourse({name: '', platform: '', status: 'not-started', progress: 0, notes: ''});
                  setShowCourseModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <div className="modal-overlay" onClick={() => setShowLanguageModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Начать изучение языка</h3>
              <button onClick={() => setShowLanguageModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Язык (английский, испанский, etc.)"
                value={newLanguage.language}
                onChange={(e) => setNewLanguage({...newLanguage, language: e.target.value})}
                className="modal-input"
              />
              <select
                value={newLanguage.level}
                onChange={(e) => setNewLanguage({...newLanguage, level: e.target.value as any})}
                className="modal-select"
              >
                <option value="beginner">Начинающий</option>
                <option value="intermediate">Средний</option>
                <option value="advanced">Продвинутый</option>
                <option value="native">Родной</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowLanguageModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newLanguage.language) {
                  setLanguages([...languages, {
                    id: Date.now(),
                    language: newLanguage.language,
                    level: newLanguage.level,
                    studyTime: 0,
                    streak: 0
                  }]);
                  setNewLanguage({language: '', level: 'beginner'});
                  setShowLanguageModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div className="modal-overlay" onClick={() => setShowProjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Создать проект</h3>
              <button onClick={() => setShowProjectModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название проекта"
                value={newProject.name}
                onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                className="modal-input"
              />
              <select
                value={newProject.type}
                onChange={(e) => setNewProject({...newProject, type: e.target.value as any})}
                className="modal-select"
              >
                <option value="art">Искусство</option>
                <option value="writing">Письмо</option>
                <option value="music">Музыка</option>
                <option value="craft">Ремесло</option>
                <option value="other">Другое</option>
              </select>
              <select
                value={newProject.status}
                onChange={(e) => setNewProject({...newProject, status: e.target.value as any})}
                className="modal-select"
              >
                <option value="planning">Планирование</option>
                <option value="in-progress">В работе</option>
                <option value="completed">Завершен</option>
              </select>
              <textarea
                placeholder="Описание проекта"
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowProjectModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newProject.name) {
                  setCreativeProjects([...creativeProjects, {
                    id: Date.now(),
                    name: newProject.name,
                    type: newProject.type,
                    status: newProject.status,
                    description: newProject.description
                  }]);
                  setNewProject({name: '', type: 'art', status: 'planning', description: ''});
                  setShowProjectModal(false);
                }
              }} className="btn btn-primary">Создать</button>
            </div>
          </div>
        </div>
      )}

      {/* Journal Modal */}
      {showJournalModal && (
        <div className="modal-overlay" onClick={() => setShowJournalModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Написать заметку</h3>
              <button onClick={() => setShowJournalModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Заголовок"
                value={newJournalEntry.title}
                onChange={(e) => setNewJournalEntry({...newJournalEntry, title: e.target.value})}
                className="modal-input"
              />
              <select
                value={newJournalEntry.mood}
                onChange={(e) => setNewJournalEntry({...newJournalEntry, mood: e.target.value as any})}
                className="modal-select"
              >
                <option value="happy">😊 Радостное</option>
                <option value="neutral">😐 Нейтральное</option>
                <option value="sad">😔 Грустное</option>
                <option value="excited">🤩 Взволнованное</option>
                <option value="anxious">😰 Тревожное</option>
              </select>
              <textarea
                placeholder="Что произошло сегодня? Как ты себя чувствуешь?"
                value={newJournalEntry.content}
                onChange={(e) => setNewJournalEntry({...newJournalEntry, content: e.target.value})}
                className="modal-textarea"
                rows={6}
              />
              <div className="tags-section">
                <h4>Теги:</h4>
                {newJournalEntry.tags.map((tag, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Тег ${index + 1}`}
                    value={tag}
                    onChange={(e) => updateJournalTag(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addJournalTag} className="btn btn-secondary">Добавить тег</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowJournalModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newJournalEntry.title && newJournalEntry.content) {
                  setJournalEntries([...journalEntries, {
                    id: Date.now(),
                    date: new Date().toISOString().split('T')[0],
                    title: newJournalEntry.title,
                    content: newJournalEntry.content,
                    mood: newJournalEntry.mood,
                    tags: newJournalEntry.tags.filter(tag => tag.trim())
                  }]);
                  setNewJournalEntry({title: '', content: '', mood: 'neutral', tags: ['']});
                  setShowJournalModal(false);
                }
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Cultural Activity Modal */}
      {showCulturalModal && (
        <div className="modal-overlay" onClick={() => setShowCulturalModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить мероприятие</h3>
              <button onClick={() => setShowCulturalModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <select
                value={newCulturalActivity.type}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, type: e.target.value as any})}
                className="modal-select"
              >
                <option value="concert">Концерт</option>
                <option value="exhibition">Выставка</option>
                <option value="theater">Театр</option>
                <option value="movie">Фильм</option>
                <option value="book">Книга</option>
                <option value="other">Другое</option>
              </select>
              <input
                type="text"
                placeholder="Название"
                value={newCulturalActivity.title}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, title: e.target.value})}
                className="modal-input"
              />
              <input
                type="date"
                value={newCulturalActivity.date}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, date: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Место (опционально)"
                value={newCulturalActivity.location}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, location: e.target.value})}
                className="modal-input"
              />
              <input
                type="number"
                placeholder="Оценка (1-5)"
                min="1"
                max="5"
                value={newCulturalActivity.rating}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, rating: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Заметки"
                value={newCulturalActivity.notes}
                onChange={(e) => setNewCulturalActivity({...newCulturalActivity, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCulturalModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newCulturalActivity.title && newCulturalActivity.date) {
                  setCulturalActivities([...culturalActivities, {
                    id: Date.now(),
                    type: newCulturalActivity.type,
                    title: newCulturalActivity.title,
                    date: newCulturalActivity.date,
                    rating: newCulturalActivity.rating ? parseInt(newCulturalActivity.rating) : undefined,
                    notes: newCulturalActivity.notes,
                    location: newCulturalActivity.location
                  }]);
                  setNewCulturalActivity({type: 'concert', title: '', date: '', rating: '', notes: '', location: ''});
                  setShowCulturalModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Fitness Goal Modal */}
      {showFitnessModal && (
        <div className="modal-overlay" onClick={() => setShowFitnessModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить цель</h3>
              <button onClick={() => setShowFitnessModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <select
                value={newFitnessGoal.type}
                onChange={(e) => setNewFitnessGoal({...newFitnessGoal, type: e.target.value as any})}
                className="modal-select"
              >
                <option value="strength">Сила</option>
                <option value="cardio">Кардио</option>
                <option value="flexibility">Гибкость</option>
                <option value="endurance">Выносливость</option>
              </select>
              <input
                type="text"
                placeholder="Название цели"
                value={newFitnessGoal.name}
                onChange={(e) => setNewFitnessGoal({...newFitnessGoal, name: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Текущий результат"
                value={newFitnessGoal.current}
                onChange={(e) => setNewFitnessGoal({...newFitnessGoal, current: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Целевой результат"
                value={newFitnessGoal.target}
                onChange={(e) => setNewFitnessGoal({...newFitnessGoal, target: e.target.value})}
                className="modal-input"
              />
              <input
                type="date"
                placeholder="Дедлайн"
                value={newFitnessGoal.deadline}
                onChange={(e) => setNewFitnessGoal({...newFitnessGoal, deadline: e.target.value})}
                className="modal-input"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowFitnessModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newFitnessGoal.name && newFitnessGoal.target) {
                  setFitnessGoals([...fitnessGoals, {
                    id: Date.now(),
                    type: newFitnessGoal.type,
                    name: newFitnessGoal.name,
                    target: newFitnessGoal.target,
                    current: newFitnessGoal.current,
                    deadline: newFitnessGoal.deadline,
                    status: 'active'
                  }]);
                  setNewFitnessGoal({type: 'strength', name: '', target: '', current: '', deadline: ''});
                  setShowFitnessModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Reflection Modal */}
      {showReflectionModal && (
        <div className="modal-overlay" onClick={() => setShowReflectionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Рефлексия</h3>
              <button onClick={() => setShowReflectionModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              {newReflectionEntry.questions.map((question, index) => (
                <div key={index} className="reflection-question">
                  <label>{question}</label>
                  <textarea
                    value={newReflectionEntry.answers[index] || ''}
                    onChange={(e) => {
                      const newAnswers = [...newReflectionEntry.answers];
                      newAnswers[index] = e.target.value;
                      setNewReflectionEntry({...newReflectionEntry, answers: newAnswers});
                    }}
                    className="modal-textarea"
                    rows={3}
                  />
                </div>
              ))}
              <textarea
                placeholder="Какие инсайты у тебя есть?"
                value={newReflectionEntry.insights}
                onChange={(e) => setNewReflectionEntry({...newReflectionEntry, insights: e.target.value})}
                className="modal-textarea"
              />
              <div className="action-items-section">
                <h4>Планы действий:</h4>
                {newReflectionEntry.actionItems.map((item, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Действие ${index + 1}`}
                    value={item}
                    onChange={(e) => updateReflectionActionItem(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addReflectionActionItem} className="btn btn-secondary">Добавить действие</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowReflectionModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                setReflectionEntries([...reflectionEntries, {
                  id: Date.now(),
                  date: new Date().toISOString().split('T')[0],
                  questions: newReflectionEntry.questions,
                  answers: newReflectionEntry.answers,
                  insights: newReflectionEntry.insights,
                  actionItems: newReflectionEntry.actionItems.filter(item => item.trim())
                }]);
                setNewReflectionEntry({
                  questions: ['Что хорошего произошло сегодня?', 'Что можно улучшить?', 'За что я благодарна?'],
                  answers: ['', '', ''],
                  insights: '',
                  actionItems: ['']
                });
                setShowReflectionModal(false);
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Social Skill Modal */}
      {showSkillModal && (
        <div className="modal-overlay" onClick={() => setShowSkillModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить навык</h3>
              <button onClick={() => setShowSkillModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название навыка"
                value={newSocialSkill.skill}
                onChange={(e) => setNewSocialSkill({...newSocialSkill, skill: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание навыка"
                value={newSocialSkill.description}
                onChange={(e) => setNewSocialSkill({...newSocialSkill, description: e.target.value})}
                className="modal-textarea"
              />
              <select
                value={newSocialSkill.improvement}
                onChange={(e) => setNewSocialSkill({...newSocialSkill, improvement: e.target.value as any})}
                className="modal-select"
              >
                <option value="beginner">Начальный</option>
                <option value="developing">Развивающийся</option>
                <option value="good">Хороший</option>
                <option value="excellent">Отличный</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSkillModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newSocialSkill.skill && newSocialSkill.description) {
                  setSocialSkills([...socialSkills, {
                    id: Date.now(),
                    skill: newSocialSkill.skill,
                    description: newSocialSkill.description,
                    practiceSessions: 0,
                    improvement: newSocialSkill.improvement
                  }]);
                  setNewSocialSkill({skill: '', description: '', improvement: 'beginner'});
                  setShowSkillModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Idea Modal */}
      {showIdeaModal && (
        <div className="modal-overlay" onClick={() => setShowIdeaModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Сохранить идею</h3>
              <button onClick={() => setShowIdeaModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название идеи"
                value={newIdea.title}
                onChange={(e) => setNewIdea({...newIdea, title: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание идеи"
                value={newIdea.description}
                onChange={(e) => setNewIdea({...newIdea, description: e.target.value})}
                className="modal-textarea"
              />
              <select
                value={newIdea.category}
                onChange={(e) => setNewIdea({...newIdea, category: e.target.value as any})}
                className="modal-select"
              >
                <option value="business">Бизнес</option>
                <option value="creative">Творчество</option>
                <option value="personal">Личное</option>
                <option value="learning">Обучение</option>
                <option value="other">Другое</option>
              </select>
              <div className="tags-section">
                <h4>Теги:</h4>
                {newIdea.tags.map((tag, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Тег ${index + 1}`}
                    value={tag}
                    onChange={(e) => updateIdeaTag(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addIdeaTag} className="btn btn-secondary">Добавить тег</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowIdeaModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newIdea.title && newIdea.description) {
                  setIdeas([...ideas, {
                    id: Date.now(),
                    title: newIdea.title,
                    description: newIdea.description,
                    category: newIdea.category,
                    status: 'idea',
                    dateAdded: new Date().toISOString().split('T')[0],
                    tags: newIdea.tags.filter(tag => tag.trim())
                  }]);
                  setNewIdea({title: '', description: '', category: 'creative', tags: ['']});
                  setShowIdeaModal(false);
                }
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Modal */}
      {showAchievementModal && (
        <div className="modal-overlay" onClick={() => setShowAchievementModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Записать достижение</h3>
              <button onClick={() => setShowAchievementModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название достижения"
                value={newAchievement.title}
                onChange={(e) => setNewAchievement({...newAchievement, title: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание достижения"
                value={newAchievement.description}
                onChange={(e) => setNewAchievement({...newAchievement, description: e.target.value})}
                className="modal-textarea"
              />
              <select
                value={newAchievement.category}
                onChange={(e) => setNewAchievement({...newAchievement, category: e.target.value as any})}
                className="modal-select"
              >
                <option value="career">Карьера</option>
                <option value="personal">Личное</option>
                <option value="health">Здоровье</option>
                <option value="learning">Обучение</option>
                <option value="creative">Творчество</option>
                <option value="social">Социальное</option>
              </select>
              <select
                value={newAchievement.importance}
                onChange={(e) => setNewAchievement({...newAchievement, importance: e.target.value as any})}
                className="modal-select"
              >
                <option value="small">Маленькая победа</option>
                <option value="medium">Достижение</option>
                <option value="big">Большой успех</option>
                <option value="milestone">Важная веха</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAchievementModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newAchievement.title && newAchievement.description) {
                  setAchievements([...achievements, {
                    id: Date.now(),
                    title: newAchievement.title,
                    description: newAchievement.description,
                    date: new Date().toISOString().split('T')[0],
                    category: newAchievement.category,
                    importance: newAchievement.importance
                  }]);
                  setNewAchievement({title: '', description: '', category: 'personal', importance: 'medium'});
                  setShowAchievementModal(false);
                }
              }} className="btn btn-primary">Записать</button>
            </div>
          </div>
        </div>
      )}

      {/* Dream Modal */}
      {showDreamModal && (
        <div className="modal-overlay" onClick={() => setShowDreamModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить мечту</h3>
              <button onClick={() => setShowDreamModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Моя мечта"
                value={newDream.dream}
                onChange={(e) => setNewDream({...newDream, dream: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание мечты"
                value={newDream.description}
                onChange={(e) => setNewDream({...newDream, description: e.target.value})}
                className="modal-textarea"
              />
              <select
                value={newDream.category}
                onChange={(e) => setNewDream({...newDream, category: e.target.value as any})}
                className="modal-select"
              >
                <option value="travel">Путешествия</option>
                <option value="career">Карьера</option>
                <option value="personal">Личное</option>
                <option value="relationships">Отношения</option>
                <option value="creative">Творчество</option>
                <option value="other">Другое</option>
              </select>
              <select
                value={newDream.priority}
                onChange={(e) => setNewDream({...newDream, priority: e.target.value as any})}
                className="modal-select"
              >
                <option value="low">Низкий приоритет</option>
                <option value="medium">Средний приоритет</option>
                <option value="high">Высокий приоритет</option>
              </select>
              <input
                type="text"
                placeholder="Сроки (опционально)"
                value={newDream.timeline}
                onChange={(e) => setNewDream({...newDream, timeline: e.target.value})}
                className="modal-input"
              />
              <div className="steps-section">
                <h4>Шаги к мечте:</h4>
                {newDream.steps.map((step, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Шаг ${index + 1}`}
                    value={step}
                    onChange={(e) => updateDreamStep(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addDreamStep} className="btn btn-secondary">Добавить шаг</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowDreamModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newDream.dream && newDream.description) {
                  setDreams([...dreams, {
                    id: Date.now(),
                    dream: newDream.dream,
                    description: newDream.description,
                    category: newDream.category,
                    priority: newDream.priority,
                    steps: newDream.steps.filter(step => step.trim()),
                    timeline: newDream.timeline
                  }]);
                  setNewDream({dream: '', description: '', category: 'personal', priority: 'medium', steps: [''], timeline: ''});
                  setShowDreamModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SelfDevelopmentPage;
